/**
 * MyLibraryBook.jsx — book-detail page for a personal library book.
 *
 * Feb 9 2026 user spec: documents arranged into books (single doc or
 * series). This page shows the book's ordered chapters, lets the
 * reader open one for full-text reading + flash-read, append new
 * chapters (type or paste), rename the book, or delete it.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Plus, X, Trash2, Edit3, FileText, Layers, Sparkles,
} from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import { FlashReadButton } from '../components/FlashReadOverlay';
import MyLibraryChapterAIPanel from '../components/MyLibraryChapterAIPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return ''; }
};

const MyLibraryBook = () => {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [openedChapter, setOpenedChapter] = useState(null);
  // Feb 12 2026 — Summarise / Quiz / Listen tools overlay for a chapter.
  const [aiChapter, setAiChapter] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/personal-library/books/${bookId}`);
      if (!r.ok) { setBook(null); return; }
      const d = await r.json();
      setBook(d.book); setChapters(d.chapters || []);
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  useEffect(() => { refresh(); }, [refresh]);

  const deleteBook = async () => {
    if (!window.confirm(`Delete "${book.title}" and all its chapters? This cannot be undone.`)) return;
    const r = await authFetch(`${BACKEND_URL}/api/personal-library/books/${bookId}`, { method: 'DELETE' });
    if (!r.ok) { toast.error('Delete failed'); return; }
    toast.success('Book deleted');
    navigate('/my-library');
  };

  if (loading) {
    return (
      <main className="sharp-bg max-w-3xl mx-auto px-4 py-6 min-h-screen">
        <p className="text-white/40 text-sm">Loading…</p>
      </main>
    );
  }
  if (!book) {
    return (
      <main className="sharp-bg max-w-3xl mx-auto px-4 py-6 min-h-screen">
        <p className="text-rose-300 text-sm">Book not found.</p>
        <button className="mt-3 text-amber-300 underline" onClick={() => navigate('/my-library')}>Back to My Library</button>
      </main>
    );
  }

  const Icon = book.is_series ? Layers : FileText;
  const accentTxt = book.is_series ? 'text-indigo-300' : 'text-amber-300';

  return (
    <main className="sharp-bg max-w-3xl mx-auto px-4 py-6 pb-32 min-h-screen" data-testid="my-library-book-page">
      <button
        type="button"
        onClick={() => navigate('/my-library')}
        className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-white/60 hover:text-white mb-5"
        data-testid="my-library-book-back"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> My Library
      </button>

      <header className="mb-6 flex items-start gap-4">
        <div className={`w-14 h-14 rounded-2xl border bg-[#12131A] ${book.is_series ? 'border-indigo-400/30' : 'border-amber-400/30'} grid place-items-center flex-shrink-0`}>
          <Icon className={`w-7 h-7 ${accentTxt}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`eyebrow ${accentTxt}`}>{book.is_series ? 'Series' : 'Single doc'}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-white tracking-tight mt-1">
            {book.title}<span className={accentTxt}>.</span>
          </h1>
          <p className="text-[11px] text-white/45 mt-1 tabular-nums">
            {book.chapter_count} {book.chapter_count === 1 ? 'chapter' : 'chapters'} · {(book.total_word_count || 0).toLocaleString()} words
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => setShowRename(true)}
            className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-white"
            aria-label="Edit"
            data-testid="my-library-book-rename"
          >
            <Edit3 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={deleteBook}
            className="p-2 rounded-full bg-rose-500/10 hover:bg-rose-500/20 text-rose-300"
            aria-label="Delete"
            data-testid="my-library-book-delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Add chapter CTA */}
      <button
        type="button"
        onClick={() => setShowAdd(true)}
        className="w-full mb-4 rounded-2xl border-2 border-dashed border-amber-400/35 hover:border-amber-300 bg-[#12131A] px-4 py-4 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/40"
        data-testid="my-library-add-chapter-btn"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-amber-400/15 border border-amber-400/30 grid place-items-center flex-shrink-0">
            <Plus className="w-4 h-4 text-amber-300" />
          </div>
          <div className="flex-1">
            <p className="eyebrow text-amber-300">Add</p>
            <p className="font-display text-base font-black text-white tracking-tight">
              {book.is_series ? `Chapter ${book.chapter_count + 1}` : 'Append section'}
            </p>
          </div>
        </div>
      </button>

      {/* Chapters list */}
      {chapters.length === 0 ? (
        <p className="text-center py-8 text-sm text-white/40" data-testid="my-library-book-empty">
          No chapters yet — add one above.
        </p>
      ) : (
        <ol className="space-y-2.5" data-testid="my-library-book-chapters">
          {chapters.map((c, i) => (
            <li
              key={c.item_id}
              className="rounded-2xl bg-[#12131A] border border-white/8 hover:border-amber-400/40 transition-colors p-3 sm:p-4 cursor-pointer"
              onClick={() => setOpenedChapter(c)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenedChapter(c); } }}
              data-testid={`my-library-chapter-${c.item_id}`}
            >
              <div className="flex items-start gap-3">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1.5 tabular-nums w-8">
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display text-base sm:text-lg font-black text-white tracking-tight truncate">{c.title}</p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/45 mt-1 tabular-nums">
                    {c.word_count} words · {c.source_type} · {fmtDate(c.created_at)}
                  </p>
                  <p className="text-xs sm:text-sm text-white/65 mt-2 line-clamp-2">{c.text}</p>
                </div>
                <FlashReadButton
                  text={c.text || ''}
                  title={c.title}
                  testid={`my-library-chapter-flash-${c.item_id}`}
                />
                {/* Feb 12 2026 — Summarise / Quiz / Listen overlay */}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setAiChapter(c); }}
                  className="inline-flex items-center gap-1 rounded-full border border-amber-400/30 bg-amber-400/10 hover:bg-amber-400/20 px-2.5 py-1 text-amber-200 text-[10px] font-black uppercase tracking-widest"
                  data-testid={`my-library-chapter-ai-${c.item_id}`}
                  title="AI tools: summary, quiz, listen"
                >
                  <Sparkles className="w-3 h-3" />
                  AI
                </button>
              </div>
            </li>
          ))}
        </ol>
      )}

      {/* Chapter reader modal */}
      {openedChapter && (
        <ChapterReader
          chapter={openedChapter}
          onClose={() => setOpenedChapter(null)}
          onOpenAi={(c) => { setOpenedChapter(null); setAiChapter(c); }}
        />
      )}

      {/* Feb 12 2026 — Summarise / Quiz / Listen panel */}
      {aiChapter && (
        <MyLibraryChapterAIPanel
          chapter={aiChapter}
          onClose={() => setAiChapter(null)}
        />
      )}

      {/* Add-chapter modal */}
      {showAdd && (
        <AddChapterModal
          book={book}
          onClose={() => setShowAdd(false)}
          onAdded={() => { setShowAdd(false); refresh(); }}
        />
      )}

      {/* Rename modal */}
      {showRename && (
        <RenameBookModal
          book={book}
          onClose={() => setShowRename(false)}
          onSaved={(updated) => { setBook(updated); setShowRename(false); }}
        />
      )}
    </main>
  );
};

// ──────────────────────────────────────────────────────────────────
const ChapterReader = ({ chapter, onClose, onOpenAi }) => (
  <div
    className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-3"
    onClick={onClose}
    data-testid="my-library-reader-modal"
  >
    <div
      className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-[#12131A] border border-white/10 p-5 sm:p-6"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <p className="eyebrow text-indigo-300">Chapter · {chapter.source_type}</p>
          <h3 className="font-display text-2xl sm:text-3xl font-black text-white tracking-tight mt-0.5">{chapter.title}</h3>
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1 tabular-nums">
            {chapter.word_count} words
          </p>
        </div>
        <FlashReadButton
          text={chapter.text || ''}
          title={chapter.title}
          testid="my-library-reader-flash-btn"
        />
        {/* Feb 12 2026 — surface AI tools (Summary / Quiz / Listen) from the reader too. */}
        <button
          type="button"
          onClick={() => onOpenAi?.(chapter)}
          className="inline-flex items-center gap-1 rounded-full border border-amber-400/30 bg-amber-400/10 hover:bg-amber-400/20 px-2.5 py-1 text-amber-200 text-[10px] font-black uppercase tracking-widest"
          data-testid="my-library-reader-ai-btn"
          title="Summary · quiz · listen"
        >
          <Sparkles className="w-3 h-3" /> AI
        </button>
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-white"
          aria-label="Close"
          data-testid="my-library-reader-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <p
        className="text-sm sm:text-base text-white/85 whitespace-pre-wrap leading-relaxed"
        style={{ fontFamily: '"Fraunces", Georgia, serif' }}
        data-testid="my-library-reader-text"
      >
        {chapter.text}
      </p>
    </div>
  </div>
);

// ──────────────────────────────────────────────────────────────────
const AddChapterModal = ({ book, onClose, onAdded }) => {
  const [title, setTitle] = useState(book.is_series ? `Chapter ${book.chapter_count + 1}` : 'New section');
  const [text, setText] = useState('');
  const [source, setSource] = useState('type'); // 'type' | 'paste'
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!text.trim()) { toast.error('Add some words'); return; }
    setBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/personal-library/books/${book.book_id}/chapters`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: title.trim() || 'Untitled chapter', text: text.trim(), source_type: source }),
      });
      if (r.status === 403) { toast.error('Premium feature'); return; }
      if (!r.ok) { toast.error('Could not save'); return; }
      toast.success('Chapter added');
      onAdded();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-3"
      onClick={onClose}
      data-testid="my-library-add-chapter-modal"
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-[#12131A] border border-white/10 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="flex-1">
            <p className="eyebrow text-amber-300">New chapter</p>
            <h3 className="font-display text-2xl font-black text-white mt-0.5 tracking-tight">Add to {book.title}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 text-white" data-testid="my-library-add-chapter-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex gap-2 mb-3" data-testid="my-library-add-chapter-source">
          <button type="button" onClick={() => setSource('type')}
            className={`flex-1 rounded-xl border px-3 py-2 text-center transition ${source === 'type' ? 'border-amber-400 bg-amber-400/10 text-amber-300' : 'border-white/10 text-white/55'}`}
          >
            ✍️ Type
          </button>
          <button type="button" onClick={() => setSource('paste')}
            className={`flex-1 rounded-xl border px-3 py-2 text-center transition ${source === 'paste' ? 'border-amber-400 bg-amber-400/10 text-amber-300' : 'border-white/10 text-white/55'}`}
          >
            📋 Paste
          </button>
        </div>

        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          className="w-full bg-[#0B0C10] border border-white/10 rounded-xl text-white text-sm px-3 py-2.5 mb-3 focus:outline-none focus:border-amber-400/50"
          data-testid="my-library-add-chapter-title"
          placeholder="Chapter title"
        />
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={source === 'paste' ? 'Paste the text here…' : 'Type the chapter here…'}
          rows={8}
          className="w-full bg-[#0B0C10] border border-white/10 rounded-xl text-white text-sm px-3 py-2.5 focus:outline-none focus:border-amber-400/50 resize-y"
          data-testid="my-library-add-chapter-text"
        />
        <p className="text-[10px] text-white/40 mt-1 tabular-nums">
          {text.split(/\s+/).filter(Boolean).length} words
        </p>

        <button
          type="button"
          onClick={submit}
          disabled={busy}
          className="w-full mt-4 bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black font-black py-3 rounded-full uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2"
          data-testid="my-library-add-chapter-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Save chapter
        </button>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────
const RenameBookModal = ({ book, onClose, onSaved }) => {
  const [title, setTitle] = useState(book.title);
  const [isSeries, setIsSeries] = useState(book.is_series);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/personal-library/books/${book.book_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: title.trim(), is_series: isSeries }),
      });
      if (!r.ok) { toast.error('Save failed'); return; }
      const d = await r.json();
      toast.success('Updated');
      onSaved(d.book);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-3"
      onClick={onClose}
      data-testid="my-library-rename-modal"
    >
      <div className="w-full max-w-lg rounded-2xl bg-[#12131A] border border-white/10 p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 mb-4">
          <div className="flex-1">
            <p className="eyebrow text-amber-300">Edit book</p>
            <h3 className="font-display text-2xl font-black text-white mt-0.5 tracking-tight">{book.title}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 text-white" data-testid="my-library-rename-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full bg-[#0B0C10] border border-white/10 rounded-xl text-white text-sm px-3 py-2.5 mb-3 focus:outline-none focus:border-amber-400/50"
          data-testid="my-library-rename-title"
        />
        <div className="flex gap-2 mb-4">
          <button type="button" onClick={() => setIsSeries(false)}
            className={`flex-1 rounded-xl border px-3 py-2 text-center transition ${!isSeries ? 'border-amber-400 bg-amber-400/10 text-amber-300' : 'border-white/10 text-white/55'}`}
            data-testid="my-library-rename-single"
          >Single doc</button>
          <button type="button" onClick={() => setIsSeries(true)}
            className={`flex-1 rounded-xl border px-3 py-2 text-center transition ${isSeries ? 'border-indigo-400 bg-indigo-400/10 text-indigo-300' : 'border-white/10 text-white/55'}`}
            data-testid="my-library-rename-series"
          >Series</button>
        </div>
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          className="w-full bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black font-black py-3 rounded-full uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2"
          data-testid="my-library-rename-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Save changes
        </button>
      </div>
    </div>
  );
};

export default MyLibraryBook;
